/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { getThirdPartyApplications } from "../../../settingsManager/services/ThirdPartyService";
import { SMS_CHARACTER_LIMIT, THIRD_PARTY_APPLICATION_TYPES } from "../../../../utility/constants";
import { TextField, InputAdornment, Tooltip, MenuItem } from "@material-ui/core";
import { isValidPhoneNumber } from "../../../../utility/phoneNumberHelpers";
import FormTextField from "../../../../components/FormTextField/FormTextField";
import { sendSMSNotification } from "../../services/OutboundSMSNotificationService";
import { useContactData } from "../../context/contactData";
import { OUTBOUND_SMS_NOTIFICATION_TOOL_TIPS } from "../../../../utility/toolTipMessages";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { Button } from "react-bootstrap";
import { redactPII } from "../../../../utility/helpers";

const COMPONENT_NAME = "CCP components/OutboundSMSNotification/OutboundSMSNotification";

/**
 * This component enables an agent to send outbound SMS notifications through previously setup third party integration pinpoint applications.
 */
const OutboundSMSNotification = ({ contact, renderToastContent }) => {
	const [pinpointApplications, setPinpointProjects] = React.useState([]);
	const { contactDataState, setContactSMSMessageForm } = useContactData();
	const [sendSettings, setSendSettings] = React.useState({
		application: {},
		destinationNumber: "", //default to contacts phone number if available
		message: "",
	});
	const [sendingMessage, setSendingMessage] = React.useState(false);
	const [loading, setLoading] = React.useState(true);
	const [formEdited, setFormEdited] = React.useState(false);
	const [clearFields, setClearFields] = React.useState(false);
	const [currentContactId, setCurrentContact] = React.useState("");
	const sendDisabled =
		!sendSettings.application.applicationId ||
		!isValidPhoneNumber(sendSettings.destinationNumber) ||
		!sendSettings.message;

	React.useEffect(() => {
		const loadApplications = async () => {
			try {
				const data = await getThirdPartyApplications(THIRD_PARTY_APPLICATION_TYPES.PINPOINT);
				setPinpointProjects(Object.values(data).filter((app) => !app.defaultApplication));
				setLoading(false);
			} catch (error) {
				v2.error(`Error while fetching pinpoint applications.`, {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_THIRD_PARTY_APPLICATIONS_ERROR,
				});
			}
		};

		loadApplications();
	}, []);

	React.useEffect(() => {
		if (!clearFields && contact && currentContactId !== contact.contactId) {
			setCurrentContact(contact.contactId);
			// On incoming SMS contacts we set the username to a phone number.
			const userName = contact.attributes.find((x) => x.name === "userName")?.value;
			const prevSettings = {
				...(contactDataState[contact.contactId]?.smsMessageFormState || {
					application: {},
					message: "",
					destinationNumber: "",
				}),
			};
			setFormEdited(
				!!contactDataState[contact.contactId]?.smsMessageFormState &&
					(Object.keys(contactDataState[contact.contactId].smsMessageFormState.application).length !== 0 ||
						contactDataState[contact.contactId]?.smsMessageFormState.destinationNumber !== "" ||
						contactDataState[contact.contactId]?.smsMessageFormState.message !== ""),
			);
			setSendSettings({
				...prevSettings,
				destinationNumber: contactDataState[contact.contactId]?.smsMessageFormState
					? prevSettings.destinationNumber
					: isValidPhoneNumber(contact.phone)
					? contact.phone
					: isValidPhoneNumber(userName)
					? userName
					: prevSettings.destinationNumber,
			});
		} else if (clearFields || !contact) {
			setClearFields(false);
			const newSettings = {
				application: {},
				destinationNumber: "",
				message: "",
			};
			setFormEdited(false);
			setSendSettings(newSettings);
			if (contact) {
				setContactSMSMessageForm(contact.contactId, newSettings);
			}
		}
	}, [contact, contactDataState, clearFields, setContactSMSMessageForm, currentContactId]);

	React.useEffect(() => {
		const sendMessage = async () => {
			try {
				const result = await sendSMSNotification({
					...sendSettings,
					agentId: window.agent ? window.agent.getConfiguration().username : "UNKNOWN AGENT ID",
				});
				if (result.success) {
					renderToastContent(true, `SMS message sent to ${sendSettings.destinationNumber}.`, "success", 30000);
				} else {
					renderToastContent(
						true,
						<div style={{ textAlign: "center" }}>
							<div>SMS Failed to send.</div>
							<div>{`Error message: ${result.results[sendSettings.destinationNumber].StatusMessage}.`}</div>
						</div>,
						"danger",
						30000,
					);
				}
			} catch (error) {
				v2.error("Error while attempting to send outbound SMS notification.", {
					additionalData: {
						error: errorToObject(error),
						sendSettings: redactPII(sendSettings, [{ path: "$.destinationNumber", keepLast: 3 }, "$.message"]),
					},
					componentName: COMPONENT_NAME,
					logCode: errorCodes.SEND_OUTBOUND_SMS_NOTIFICATION_ERROR,
				});
				renderToastContent(true, `Something went wrong.`, "danger");
			} finally {
				setSendingMessage(false);
				setLoading(false);
			}
		};
		if (!loading && sendingMessage) {
			setLoading(true);
			sendMessage();
		}
	}, [sendingMessage, loading, sendSettings, renderToastContent]);

	const updateSendSettings = React.useCallback(
		(valueName, value) => {
			if (!formEdited) {
				setFormEdited(true);
			}
			setSendSettings((oldSendSettings) => {
				return { ...oldSendSettings, [valueName]: value };
			});
		},
		[formEdited],
	);

	const updateContactState = (messageFormOverride) => {
		if (contact) {
			setContactSMSMessageForm(contact.contactId, messageFormOverride || sendSettings);
		}
	};

	return (
		<div style={{ padding: "10px", display: "flex", flexDirection: "column", flex: 1 }}>
			<div>
				<TextField
					label={"Origin Phone Number"}
					title={sendSettings.application.applicationId || undefined}
					select
					style={{ margin: "25px", minWidth: "40%" }}
					value={sendSettings.application.applicationId || ""}
					error={formEdited && !sendSettings.application.applicationId}
					onChange={(event) => {
						const edited = sendSettings.application.applicationId !== event.target.value;
						if (edited) {
							updateSendSettings(
								"application",
								pinpointApplications.find((x) => x.applicationId === event.target.value),
							);
							setFormEdited(true);
						}
					}}
					onBlur={() => updateContactState()}
					helperText={!sendSettings.application.applicationId && "Please select a phone number."}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Tooltip
									arrow
									interactive
									placement={"bottom-start"}
									title={OUTBOUND_SMS_NOTIFICATION_TOOL_TIPS.ORIGIN_PHONE_NUMBER}
								>
									<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
								</Tooltip>
							</InputAdornment>
						),
					}}
				>
					{(pinpointApplications || []).map((app) => {
						return (
							<MenuItem key={app.applicationId} value={app.applicationId} title={app.applicationId}>
								<div>
									<span style={{ marginRight: "10px" }}>{app.friendlyName}</span>{" "}
									<span style={{ color: "gray" }}>({app.applicationId})</span>
								</div>
							</MenuItem>
						);
					})}
				</TextField>
				<FormTextField
					label={"Destination Phone Number"}
					formEdited={formEdited}
					setEdited={setFormEdited}
					startingValue={sendSettings.destinationNumber || ""}
					setConfigValue={updateSendSettings}
					valueName={"destinationNumber"}
					onBlurEvent={() => updateContactState()}
					enableDebounce
					toolTipMessage={OUTBOUND_SMS_NOTIFICATION_TOOL_TIPS.DESTINATION_PHONE_NUMBER}
					errorMessage={"A valid destination phone number is required."}
					errorCheck={(newValue) => {
						if (!formEdited) {
							return false;
						}
						if (newValue) {
							return !isValidPhoneNumber(newValue);
						}
						return true;
					}}
				/>
			</div>
			<div style={{ padding: "0px 25px", display: "flex", flexDirection: "column", flex: 1 }}>
				<label>Message:</label>
				<textarea
					className="form-control"
					style={{
						width: "100%",
						resize: "none",
						padding: "10px",
						flex: 9,
					}}
					value={sendSettings.message}
					maxLength={SMS_CHARACTER_LIMIT}
					onChange={(e) => updateSendSettings("message", e.target.value)}
					onBlur={() => updateContactState()}
				></textarea>
				<div style={{ textAlign: formEdited && sendSettings.message ? "right" : "left", flex: 1 }}>
					<label style={{ fontSize: "12px", marginTop: "2px" }}>
						{sendSettings.message ? (
							SMS_CHARACTER_LIMIT - sendSettings.message.length + " characters remaining."
						) : formEdited ? (
							<span style={{ color: "red" }}>A message is required</span>
						) : (
							<span></span>
						)}
					</label>
				</div>
			</div>

			<div style={{ display: "flex", justifyContent: "space-between", marginRight: "25px", height: "75px" }}>
				<div />
				{sendingMessage ? (
					<div>
						<i style={{ height: "30px" }} className="fas fa-spinner loading-spinner spin-animation" />
					</div>
				) : (
					<div>
						<Button
							size="sm"
							variant={"outline-secondary"}
							onClick={() => {
								setClearFields(true);
							}}
							style={{
								opacity: 1,
								marginLeft: 10,
							}}
						>
							Clear Fields
						</Button>
						<Button
							disabled={sendDisabled || sendingMessage}
							size="sm"
							variant="outline-secondary"
							style={{
								marginLeft: 10,
							}}
							onClick={() => {
								setSendingMessage(true);
							}}
						>
							Send Message
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};
export default OutboundSMSNotification;
